import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpEventType,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  toModel,
  toModelArray,
} from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  jsonToFormData,
} from '@kinet-open-network/client/shared/util';
import { apiFilter } from '@kinet-open-network/shared/data-access/api';
import {
  AttachmentType,
  IAttachment,
  ICreateAttachmentData,
} from '@kinet-open-network/shared/data-access/model';
import { Observable, Observer, Subscriber, firstValueFrom, map } from 'rxjs';
import { Attachment } from './attachment.model';

@Injectable({
  providedIn: 'root',
})
export class AttachmentsService {
  private http = inject(HttpClient);

  getById(
    id: string,
    options?: { context?: HttpContext },
  ): Observable<Attachment> {
    return this.http
      .get<{ data: IAttachment }>(`/attachments/${id}`, {
        ...options,
      })
      .pipe(map(({ data }) => toModel(data, Attachment)));
  }

  getByIds(ids: string[]): Observable<Attachment[]> {
    return this.http
      .get<{ data: IAttachment[] }>(`/attachments`, {
        params: {
          filters: apiFilter<IAttachment>().id.in(ids).toString(),
          perPage: -1,
        },
      })
      .pipe(map(({ data }) => toModelArray(data, Attachment)));
  }

  create<T extends AttachmentType = AttachmentType.LOCAL>(
    data: Partial<ICreateAttachmentData<T>>,
    fileOrBase64: Blob | string,
    onProgress?: (progress: number) => void,
  ): Observable<Attachment> {
    if (typeof fileOrBase64 === 'string') {
      return this.uploadBase64(data, fileOrBase64, onProgress);
    } else {
      return this.uploadFile(data, fileOrBase64, onProgress);
    }
  }

  getPublicDownloadUrl(attachmentId: string) {
    return this.http
      .get<{ link: string }>(`/attachments/public/${attachmentId}/link`)
      .pipe(map(({ link }) => link));
  }

  private uploadBase64<T extends AttachmentType = AttachmentType.LOCAL>(
    data: Partial<ICreateAttachmentData<T>>,
    base64: string,
    onProgress?: (progress: number) => void,
  ) {
    return new Observable<Attachment>((observer) => {
      const internalSubscription = this.http
        .post<{ data: IAttachment }>(
          `/attachments`,
          { ...data, data: base64 },
          {
            observe: 'events',
            reportProgress: true,
            responseType: 'json',
          },
        )
        .subscribe(this.observeUploadProgress(observer, onProgress));

      return () => internalSubscription.unsubscribe();
    });
  }

  private observeUploadProgress(
    parentObserver: Subscriber<Attachment>,
    onProgress?: (progress: number) => void,
  ): Observer<HttpEvent<{ data: IAttachment }>> {
    return {
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // Handle progress if needed
          const progress = event.total
            ? Math.round((100 * event.loaded) / event.total)
            : 0;

          if (onProgress) {
            onProgress(progress);
          }
          return;
        }

        if (event.type === HttpEventType.Response) {
          if (!event.body?.data) {
            parentObserver.error(
              new Error('Nastala chyba pri nahrávaní súboru'),
            );
            return;
          }

          const attachment = toModel({ ...event.body.data }, Attachment);
          parentObserver.next(attachment);
          parentObserver.complete();
        }
      },
      error: (err) => parentObserver.error(err),
      complete: () => {},
    };
  }

  private uploadFile<T extends AttachmentType = AttachmentType.LOCAL>(
    data: Partial<ICreateAttachmentData<T>>,
    file: Blob,
    onProgress?: (progress: number) => void,
  ) {
    // const formData = jsonToFormData(data);
    // formData.append('file', file);

    // return this.http
    //   .post<{ data: IAttachment }>(`/attachments`, formData, {
    //     observe: 'events',
    //     reportProgress: true,
    //     responseType: 'json',
    //   })
    //   .pipe(
    //     map((event) => {
    //       if (event.type === HttpEventType.UploadProgress) {
    //         const progress = event.total
    //           ? Math.round((100 * event.loaded) / event.total)
    //           : 0;
    //         if (onProgress) {
    //           onProgress(progress);
    //         }
    //       }

    //       if (event.type === HttpEventType.Response) {
    //         if (!event.body?.data) {
    //           throw new Error('Nastala chyba pri nahrávaní súboru');
    //         }

    //         return toModel({ ...event.body.data }, Attachment);
    //       }

    //       return null;
    //     }),
    //     filter((attachment): attachment is Attachment => attachment !== null),
    //   );

    return new Observable<Attachment>((observer) => {
      const formData = jsonToFormData(data);
      formData.append('file', file);

      const internalSubscription = this.http
        .post<{ data: IAttachment }>(`/attachments`, formData, {
          observe: 'events',
          reportProgress: true,
          // responseType: 'json',
        })
        .subscribe(this.observeUploadProgress(observer, onProgress));

      return () => internalSubscription.unsubscribe();
    });
  }

  delete(id: string, force = false): Observable<void> {
    return this.http.delete<void>(`/attachments/${id}`, {
      body: {
        force,
      },
    });
  }

  async download(id: string, name: string | null) {
    return firstValueFrom(
      this.http.get(`/attachments/${id}/download`, {
        responseType: 'blob',
        observe: 'response',
      }),
    ).then((file) => {
      const blob = file.body as Blob;
      return downloadBlob(blob, name ?? 'download');
    });
  }
}
